import { useQueryClient } from "@tanstack/react-query";
import { Error, Hint, Label } from "@tql/ui-components-react/kendo/labels";
import { filterClearIcon } from "@tql/ui-components-react/kendo/svg-icons";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { DatePicker } from "@tql/ui-components-react/tql/dateinputs";
import {
	refreshCcw_03,
	share_04Line,
} from "@tql/ui-components-react/untitled/icons";
import {
	addDays,
	format,
	isBefore,
	isValid,
	parse,
	startOfDay,
	subDays,
	subYears,
} from "date-fns";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { convertToGridColumnProps } from "~/features/quote-board/utils/quote-board-column-processor.ts";
import { ExportReportModal } from "~/features/reports/components/export-report-modal/export-report-modal.tsx";
import { ColumnChooser } from "~/shared/components/column-chooser/column-chooser.tsx";
import { formatDateToNumericUTC } from "~/shared/components/trax-grid/date-cell/date-cell.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useGridFilterTabNavigation } from "~/shared/hooks/use-grid-filter-tab-navigation.ts";
import { useGridState } from "~/shared/hooks/use-grid-state.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import { getDefaultShipmentDetailReportGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

import { useGetInternalUserPreferences } from "../../../profile/hooks/internal/use-get-internal-user-preferences.ts";
import { useInternalGridPreferencesSync } from "../../../profile/hooks/internal/use-internal-grid-preferences-sync.ts";
import { useGetUserPreferences } from "../../../profile/hooks/use-get-user-preferences.ts";
import { useGridPreferencesSync } from "../../../profile/hooks/use-grid-preferences-sync.ts";
import {
	formatCountryCode,
	formatTimeFromDateTime,
} from "../../constants/shipment-detail-columns.tsx";
import { useReportsFeatureAccess } from "../../hooks/use-reports-feature-access.ts";
import { useShipmentDetailReportColumns } from "../../hooks/use-shipment-detail-report-columns.ts";
import { useShipmentDetailsReport } from "../../hooks/use-shipment-details-report.ts";
import { MobileShipmentDetailsReport } from "../mobile-shipment-details-report/mobile-shipment-details-report.tsx";
import styles from "./shipment-details-report.module.css";

type ShipmentDetailsReportProps = {
	customerId: number | undefined;
	isCustomerLoading: boolean;
	readonly mobileHeader?: ReactNode;
};

const DATE_FIELDS = new Set([
	"deliveryApptDateTime",
	"loadDate",
	"pickupApptDateTime",
	"actualPickupDateTime",
	"actualDeliveryDateTime",
]);

const CURRENCY_FIELDS = new Set(["totalBillAmount"]);

const COUNTRY_FIELDS = new Set(["originCountryCode", "destinationCountryCode"]);

const TIME_FIELDS = new Set(["scheduledPickupTime", "scheduledDeliveryTime"]);

const getAvailableShipmentDetailColumns = (
	columns: BoardColumn[],
	canSeeTotalBillAmount: boolean,
): BoardColumn[] =>
	columns.filter((column) =>
		canSeeTotalBillAmount ? true : column.field !== "totalBillAmount",
	);

const getVisibleShipmentDetailColumns = (
	columns: BoardColumn[],
): BoardColumn[] => columns.filter((column) => column.show);

const formatCsvValue = (field: string, value: unknown): unknown => {
	if (DATE_FIELDS.has(field) && typeof value === "string") {
		return formatDateToNumericUTC(value);
	}
	if (CURRENCY_FIELDS.has(field) && typeof value === "number") {
		return formatCurrency(value);
	}
	if (COUNTRY_FIELDS.has(field) && typeof value === "string") {
		return formatCountryCode(value);
	}
	if (TIME_FIELDS.has(field) && typeof value === "string") {
		return formatTimeFromDateTime(value);
	}
	return value;
};

export function ShipmentDetailsReport({
	customerId,
	isCustomerLoading,
	mobileHeader,
}: Readonly<ShipmentDetailsReportProps>) {
	const {
		filter,
		handleFilterChange,
		handleClearFilters,
		skip,
		take,
		handlePageChange,
		gridKey,
		isLoadingPreference,
	} = useGridState(GridIdentifiers["Shipment Details Report"], {
		enableFilter: true,
		enableClearFilters: true,
	});

	const { data: canSeeTotalBillAmount = false } =
		useReportsFeatureAccess(customerId);

	useGridFilterTabNavigation();

	const isMobile = useIsMobile();

	const [showExportModal, setShowExportModal] = useState(false);

	const [searchParams, setSearchParams] = useSearchParams();

	const dateRange = useMemo(() => {
		const startParam = searchParams.get("startDate");
		const endParam = searchParams.get("endDate");

		const parsedStart = startParam
			? parse(startParam, "yyyy-MM-dd", new Date())
			: null;
		const parsedEnd = endParam
			? parse(endParam, "yyyy-MM-dd", new Date())
			: null;

		return {
			start:
				parsedStart && isValid(parsedStart)
					? startOfDay(parsedStart)
					: startOfDay(subDays(new Date(), 1)),
			end:
				parsedEnd && isValid(parsedEnd)
					? startOfDay(parsedEnd)
					: startOfDay(new Date()),
		};
	}, [searchParams]);

	const setDateRange = useCallback(
		(update: { start?: Date | null; end?: Date | null }) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if ("start" in update) {
						if (update.start) {
							next.set("startDate", format(update.start, "yyyy-MM-dd"));
						} else {
							next.delete("startDate");
						}
					}
					if ("end" in update) {
						if (update.end) {
							next.set("endDate", format(update.end, "yyyy-MM-dd"));
						} else {
							next.delete("endDate");
						}
					}
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	const [localStart, setLocalStart] = useState<Date | null>(dateRange.start);
	const [localEnd, setLocalEnd] = useState<Date | null>(dateRange.end);

	useEffect(() => {
		setLocalStart(dateRange.start);
	}, [dateRange.start]);

	useEffect(() => {
		setLocalEnd(dateRange.end);
	}, [dateRange.end]);

	const handleStartDateChange = useCallback((event: { value: Date | null }) => {
		setLocalStart(event.value);
	}, []);

	const handleEndDateChange = useCallback((event: { value: Date | null }) => {
		setLocalEnd(event.value);
	}, []);

	const handleStartDateBlur = useCallback(() => {
		if (localStart && isValid(localStart)) {
			setDateRange({ start: localStart });
		}
	}, [localStart, setDateRange]);

	const handleEndDateBlur = useCallback(() => {
		if (localEnd && isValid(localEnd)) {
			setDateRange({ end: localEnd });
		}
	}, [localEnd, setDateRange]);

	const minDate = useMemo(
		() => addDays(subYears(startOfDay(new Date()), 2), 1),
		[],
	);

	const isStartAfterEnd = useMemo(() => {
		if (!dateRange.start || !dateRange.end) return false;
		return isBefore(dateRange.end, dateRange.start);
	}, [dateRange.start, dateRange.end]);

	const isStartBeforeMinDate = useMemo(() => {
		if (!dateRange.start) return false;
		return isBefore(dateRange.start, minDate);
	}, [dateRange.start, minDate]);

	const isDateRangeValid = useMemo(() => {
		if (!dateRange.start || !dateRange.end) return false;
		return !isStartBeforeMinDate && !isStartAfterEnd;
	}, [dateRange.start, dateRange.end, isStartBeforeMinDate, isStartAfterEnd]);

	const dateRangeMessage = useMemo(() => {
		if (isStartAfterEnd) {
			return {
				text: "Start date must be before or equal to end date.",
				isError: true,
			};
		}
		if (isStartBeforeMinDate) {
			return {
				text: "Selected dates are outside the allowed range.",
				isError: true,
			};
		}
		return {
			text: "Date range is limited to a maximum of the last 2 years.",
			isError: false,
		};
	}, [isStartAfterEnd, isStartBeforeMinDate]);

	const { data, isFetching, isError } = useShipmentDetailsReport({
		customerId,
		startDate: dateRange.start,
		endDate: dateRange.end,
		enabled: isDateRangeValid,
	});

	const enrichedData = useMemo(
		() =>
			data?.map((item) => ({
				...item,
				scheduledPickupTime: item.pickupApptDateTime,
				scheduledDeliveryTime: item.deliveryApptDateTime,
			})),
		[data],
	);

	const queryClient = useQueryClient();

	const { isInternal } = useDeploymentType();

	// Internal and external users both use the server-backed preference path
	// (visibility, order, width) via independent endpoints/hooks. Both data and
	// sync hooks are called unconditionally; each self-disables its network
	// request for the other deployment, and the active set is selected by
	// deployment type. The external flow is left fully untouched.
	const { data: externalUserPreferences } = useGetUserPreferences(
		GridIdentifiers["Shipment Details Report"],
	);
	const { data: internalUserPreferences } = useGetInternalUserPreferences(
		GridIdentifiers["Shipment Details Report"],
	);
	const userPreferences = isInternal
		? internalUserPreferences
		: externalUserPreferences;

	const selectedColumns = useShipmentDetailReportColumns({
		columnPrefs: userPreferences?.columns,
	});

	const gridPrefsOptions = {
		defaultPrefs:
			getDefaultShipmentDetailReportGridPrefs(isInternal)[
				GridIdentifiers["Shipment Details Report"]
			]?.columns,
	};
	const externalGridPrefs = useGridPreferencesSync(
		GridIdentifiers["Shipment Details Report"],
		gridPrefsOptions,
	);
	const internalGridPrefs = useInternalGridPreferencesSync(
		GridIdentifiers["Shipment Details Report"],
		gridPrefsOptions,
	);
	const {
		gridKey: prefsGridKey,
		onColumnVisibilityChange: handleToggleColumn,
		onColumnReorder: handleColumnReorder,
		onColumnResize: handleColumnResize,
		resetColumns: handleResetColumns,
	} = isInternal ? internalGridPrefs : externalGridPrefs;

	const availableColumns = useMemo(
		() =>
			getAvailableShipmentDetailColumns(selectedColumns, canSeeTotalBillAmount),
		[selectedColumns, canSeeTotalBillAmount],
	);

	const processedColumns = useMemo(
		() => getVisibleShipmentDetailColumns(availableColumns),
		[availableColumns],
	);

	// Both deployments render at their persisted/canonical widths; internal
	// width auto-distribution was removed so saved widths are honored.
	const gridColumns = processedColumns;

	const handleClickRefresh = () => {
		void queryClient.invalidateQueries({
			queryKey: ["shipmentDetailsReport"],
		});
	};

	const handleClickClearFilters = () => {
		handleClearFilters?.();
		handleResetColumns();
		setSearchParams({}, { replace: true });
	};

	return (
		<>
			{isMobile ? (
				<div className={styles.shipmentDetailsMobileControlsCard}>
					{mobileHeader}
					<div className={styles.shipmentDetailsToolbarMobile}>
						<div>
							<Label>Delivery Date Range</Label>
							<div className={styles.shipmentDetailsDateRange}>
								<DatePicker
									format={"MM/dd/yyyy"}
									min={minDate}
									onBlur={handleStartDateBlur}
									onChange={handleStartDateChange}
									value={localStart}
									width={120}
								/>
								<DatePicker
									format={"MM/dd/yyyy"}
									min={minDate}
									onBlur={handleEndDateBlur}
									onChange={handleEndDateChange}
									value={localEnd}
									width={120}
								/>
							</div>
						</div>
						<Hint className={styles.shipmentDetailsMobileHint}>
							{dateRangeMessage.text}
						</Hint>
						<div className={styles.shipmentDetailsToolbarMobileActions}>
							<Button
								onClick={handleClickRefresh}
								svgIcon={refreshCcw_03}
								title={"Refresh"}
								variant={"tertiary"}
							/>
							<Button
								onClick={handleClickClearFilters}
								svgIcon={filterClearIcon}
								title={"Clear all filters"}
								variant={"tertiary"}
							/>
							<Button
								onClick={() => setShowExportModal(true)}
								svgIcon={share_04Line}
								variant={"primary"}
							>
								Export
							</Button>
						</div>
					</div>
				</div>
			) : (
				<div className={styles.shipmentDetailsToolbar}>
					<div className={styles.shipmentDetailsToolbarLeft}>
						<div>
							<Label>Delivery Date Range</Label>
							<div className={styles.shipmentDetailsDateRange}>
								<DatePicker
									format={"MM/dd/yyyy"}
									min={minDate}
									onBlur={handleStartDateBlur}
									onChange={handleStartDateChange}
									value={localStart}
									width={120}
								/>
								<DatePicker
									format={"MM/dd/yyyy"}
									min={minDate}
									onBlur={handleEndDateBlur}
									onChange={handleEndDateChange}
									value={localEnd}
									width={120}
								/>
							</div>
						</div>
						<Button
							onClick={() => setShowExportModal(true)}
							svgIcon={share_04Line}
							variant={"primary"}
						>
							Export
						</Button>
					</div>
					<div className={styles.shipmentDetailsToolbarRight}>
						<Button
							onClick={handleClickRefresh}
							svgIcon={refreshCcw_03}
							title={"Refresh"}
							variant={"tertiary"}
						/>
						<Button
							onClick={handleClickClearFilters}
							svgIcon={filterClearIcon}
							title={"Clear all filters"}
							variant={"tertiary"}
						/>
						<ColumnChooser
							secondary
							columns={availableColumns}
							onColumnToggle={handleToggleColumn}
							onResetColumns={handleResetColumns}
						/>
					</div>
				</div>
			)}
			{!isMobile && !dateRangeMessage.isError ? (
				<Hint>{dateRangeMessage.text}</Hint>
			) : null}
			{dateRangeMessage.isError ? <Error>{dateRangeMessage.text}</Error> : null}
			{isMobile ? (
				<MobileShipmentDetailsReport
					data={enrichedData ?? []}
					isError={isError}
					isLoading={isFetching || isCustomerLoading}
					showRate={canSeeTotalBillAmount}
				/>
			) : (
				<>
					{isError ? (
						<div className={styles.shipmentDetailsError}>
							Failed to load report data. Please try again.
						</div>
					) : null}
					<div className={styles.shipmentDetailsGridWrapper}>
						<TraxGrid
							key={`${gridKey}-${prefsGridKey}`}
							columns={convertToGridColumnProps(gridColumns)}
							data={enrichedData ?? []}
							dataItemKey={"poNumber"}
							filter={filter}
							filterable={true}
							loading={isFetching || isCustomerLoading || isLoadingPreference}
							onColumnReorder={handleColumnReorder}
							onColumnResize={handleColumnResize}
							onFilterChange={handleFilterChange}
							onPageChange={handlePageChange}
							reorderable={true}
							skip={skip}
							style={{ height: "100%" }}
							take={take}
						/>
					</div>
				</>
			)}
			{showExportModal ? (
				<ExportReportModal
					allColumns={availableColumns}
					data={(enrichedData ?? []) as Record<string, unknown>[]}
					filename={"shipment-details-report.csv"}
					filter={filter}
					formatValue={formatCsvValue}
					onClose={() => setShowExportModal(false)}
					selectedColumns={availableColumns}
				/>
			) : null}
		</>
	);
}
